import { Suspense } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { startPath } from '../../config/routes.js';
import { hatMarktplatzZugang } from '../../lib/aboAccess.js';
import { ErrorBoundary } from '../ui/ErrorBoundary';
import FahrerDataProvider from '../../contexts/FahrerDataContext.jsx';
import { retryLazy } from '../../lib/retryLazy';
import { istFirmenZugang, istMitarbeiter, darf, istChef } from '../../lib/zugang.js';

// ═══ Lazy-loaded Pages ═══
// Pages sind .tsx (typisiert seit TS-Migration 17.04.2026). Die Importe
// schreiben weiterhin `.jsx` — Vite löst das via ts-migration-resolve-Plugin
// (vite.config.js) zu `.tsx` auf. `ComponentType<any>` hier ist nur der
// Lazy-Wrapper-Type; echte Props-Validation passiert beim JSX-Aufruf, da
// die `.tsx`-Files ihren typisierten default-Export behalten.
// (Aktualisiert 22.05.2026)
const AboPage = retryLazy<ComponentType<any>>(() => import('../../pages/abo/AboPage.jsx'));
const FirmaAuftraege = retryLazy<ComponentType<any>>(() => import('../../pages/firma/FirmaAuftraege.jsx'));
const Abrechnung = retryLazy<ComponentType<any>>(() => import('../../pages/firma/Abrechnung.jsx'));
const FirmaFavoriten = retryLazy<ComponentType<any>>(() => import('../../pages/firma/FirmaFavoriten.jsx'));
const TeamSeite = retryLazy<ComponentType<any>>(() => import('../../pages/firma/TeamSeite.jsx'));
const FirmaDashboard = retryLazy<ComponentType<any>>(() => import('../../pages/firma/FirmaDashboard.jsx'));
const FirmaAdressbuch = retryLazy<ComponentType<any>>(() => import('../../pages/firma/FirmaAdressbuch.jsx'));
const FahrerDashboard = retryLazy<ComponentType<any>>(() => import('../../pages/fahrer/FahrerDashboard.jsx'));
const FahrerAuftraege = retryLazy<ComponentType<any>>(() => import('../../pages/fahrer/FahrerAuftraege.jsx'));
const FahrerEigeneAuftraege = retryLazy<ComponentType<any>>(() => import('../../pages/fahrer/FahrerEigeneAuftraege.jsx'));
const FahrerFahrtenbuch = retryLazy<ComponentType<any>>(() => import('../../pages/fahrer/FahrerFahrtenbuch.jsx'));
const FahrerFinanzen = retryLazy<ComponentType<any>>(() => import('../../pages/fahrer/FahrerFinanzen.jsx'));
const FahrerRechnungErstellen = retryLazy<ComponentType<any>>(() => import('../../pages/fahrer/FahrerRechnungErstellen.jsx'));
const FahrerZertifikate = retryLazy<ComponentType<any>>(() => import('../../pages/fahrer/FahrerZertifikate.jsx'));
const Auszahlung = retryLazy<ComponentType<any>>(() => import('../../pages/fahrer/Auszahlung.jsx'));
const FahrerKunden = retryLazy<ComponentType<any>>(() => import('../../pages/fahrer/FahrerKunden.jsx'));
const Tourenplaner = retryLazy<ComponentType<any>>(() => import('../../pages/fahrer/Tourenplaner.jsx'));
const Marktplatz = retryLazy<ComponentType<any>>(() => import('../../pages/marktplatz/Marktplatz.jsx'));
const MeineAngebote = retryLazy<ComponentType<any>>(() => import('../../pages/marktplatz/MeineAngebote.jsx'));
const ProfilSeite = retryLazy<ComponentType<any>>(() => import('../../pages/profil/ProfilSeite.jsx'));
const MitarbeiterProfil = retryLazy<ComponentType<any>>(() => import('../../pages/profil/MitarbeiterProfil.jsx'));
const RoteListe = retryLazy<ComponentType<any>>(() => import('../../pages/profil/RoteListe.jsx'));
const UeberUns = retryLazy<ComponentType<any>>(() => import('../../pages/profil/UeberUns.jsx'));
const BewertungenPage = retryLazy<ComponentType<any>>(() => import('../../pages/profil/BewertungenPage.jsx'));
const AdminDashboard = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminDashboard.jsx'));
const AdminUsers = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminUsers.jsx'));
const AdminAuftraege = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminAuftraege.jsx'));
const AdminAbos = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminAbos.jsx'));
const AdminSettings = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminSettings.jsx'));
const AdminAuditLog = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminAuditLog.jsx'));
const AdminFinanzen = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminFinanzen.jsx'));
const AdminBewertungen = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminBewertungen.jsx'));
const AdminReEngagement = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminReEngagement.jsx'));
const AdminMap = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminMap.jsx'));
const AdminMonitoring = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminMonitoring.jsx'));
const AdminUsageAnalytics = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminUsageAnalytics.jsx'));
const AdminHealthCheck = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminHealthCheck.jsx'));
const AdminConversionFunnel = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminConversionFunnel.jsx'));
const AdminBlacklist = retryLazy<ComponentType<any>>(() => import('../../pages/admin/AdminBlacklist.jsx'));
const AboGate = retryLazy<ComponentType<any>>(() =>
  import('../ui/AboGate.jsx').then((m) => ({ default: m.AboGate })),
);

const Spinner = (
  <div
    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
    <div
      style={{
        width: 28,
        height: 28,
        border: '3px solid var(--border)',
        borderTopColor: 'var(--accent)',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
  </div>
);

interface RouteGuardProps {
  children: ReactNode;
}

/** GatedRoute — Zeigt AboGate wenn User keinen Marktplatz-Zugang hat. */
function GatedRoute({ children }: RouteGuardProps) {
  const app = useApp();
  if (!app.user) return null;
  const gated = !hatMarktplatzZugang(app.user) && app.user.rolle !== 'admin';
  if (gated) {
    return <AboGate user={app.user} onNav={app.onNav} />;
  }
  return <>{children}</>;
}

/**
 * AdminRoute — Nur für Admin-Rolle. Alle anderen → Dashboard.
 * Security: Least Privilege — verhindert UI-Zugriff auf Admin-Seiten.
 */
function AdminRoute({ children }: RouteGuardProps) {
  const app = useApp();
  if (!app.user) return null;
  if (app.user.rolle !== 'admin') {
    return <Navigate to="/dashboard" replace={true} />;
  }
  return <>{children}</>;
}

/**
 * KeinMitarbeiterRoute — Chef- und Solo-Fahrer-Bereiche sind für
 * Mitarbeiter-Zugänge (DRPX-Pro, 16.10.2026) gesperrt, auch wenn jemand die
 * Adresse direkt eintippt: Abo, Rote Liste, Zertifikate hochladen und die
 * Seiten des eigenen Fahrer-Geschäfts. Weiter aufs Dashboard.
 */
function KeinMitarbeiterRoute({ children }: RouteGuardProps) {
  const app = useApp();
  if (!app.user) return null;
  if (istMitarbeiter(app.user)) {
    return <Navigate to="/dashboard" replace={true} />;
  }
  return <>{children}</>;
}

/**
 * AbrechnungRoute — Mitarbeiter ohne Recht „Abrechnung sehen" (Migration
 * 141) kommen nicht auf die Abrechnung, auch nicht per Adresse.
 */
function AbrechnungRoute({ children }: RouteGuardProps) {
  const app = useApp();
  if (!app.user) return null;
  if (!darf(app.user, 'abrechnung')) return <Navigate to="/dashboard" replace={true} />;
  return <>{children}</>;
}

/**
 * TeamRoute — Team-Seite nur für den Chef einer Firma (Rolle firma). Ohne
 * DRPX-Pro zeigt die Seite das Pro-Angebot (Lieferung 3a). Alle anderen
 * landen auf dem Dashboard.
 */
function TeamRoute({ children }: RouteGuardProps) {
  const app = useApp();
  if (!app.user) return null;
  if (!istChef(app.user)) return <Navigate to="/dashboard" replace={true} />;
  return <>{children}</>;
}

/**
 * ProfilRoute — Mitarbeiter sehen ihr eigenes, kurzes Profil (Name, Firma,
 * Abmelden). Firmendaten, Bank, Verifizierung und Konto löschen gehören dem
 * Chef (DRPX-Pro, 16.10.2026).
 */
function ProfilRoute() {
  const app = useApp();
  if (!app.user) return null;
  if (istMitarbeiter(app.user)) return <MitarbeiterProfil />;
  return <ProfilSeite />;
}

/** DashboardRoute — Rendert Firma- oder Fahrer-Dashboard je nach Rolle. */
function DashboardRoute() {
  const app = useApp();
  if (!app.user) return null;
  // Admins gehören auf ihr eigenes Dashboard (02.09.2026).
  //
  // Bis dahin fiel ein Admin in den Fahrer-Zweig und bekam ein persönliches
  // Fahrtenbuch samt Banner „Marktplatz-Zugang freischalten" — mit der
  // Aufforderung, einen Gewerbeschein hochzuladen, um bieten zu können.
  // Für ein Admin-Konto ergibt das keinen Sinn.
  //
  // Aufgefallen ist es über ein falsches Sprungziel in einer Benachrichtigung,
  // das auf '/dashboard' zurückfiel. Das Sprungziel ist repariert; diese
  // Weiterleitung schliesst den Weg generell — etwa bei alten Lesezeichen
  // oder älteren Benachrichtigungen, die noch in der Glocke liegen.
  if (app.user.rolle === 'admin') return <Navigate to="/admin" replace={true} />;
  if (istFirmenZugang(app.user)) return <FirmaDashboard />;
  return <FahrerDashboard />;
}

/**
 * ProtectedRoutes — Alle authentifizierten Seiten als React Router Routes.
 * Ersetzt den alten switch/case-PageRouter (Datei am 04.06.2026 entfernt).
 *
 * Für Fahrer wird `FahrerDataProvider` darüber gewrappt (lädt Kunden,
 * Eigene Aufträge, etc. einmalig).
 */
export function ProtectedRoutes() {
  const location = useLocation();
  const { user } = useApp();
  const isFahrer = user && user.rolle === 'fahrer';
  const content = (
    <div
      className="main-content page-transition"
      style={{ padding: 28, maxWidth: 1060, margin: '0 auto', animation: 'fadeIn .3s ease' }}>
      <ErrorBoundary resetKey={location.pathname}>
        <Suspense fallback={Spinner}>
          <Routes>
            {/* ═══ Dashboard (Firma / Fahrer) ═══ */}
            <Route path="/dashboard" element={<DashboardRoute />} />
            {/* ═══ Firma ═══ */}
            <Route
              path="/auftraege"
              element={<GatedRoute><FirmaAuftraege /></GatedRoute>} />
            <Route path="/adressbuch" element={<FirmaAdressbuch />} />
            <Route
              path="/abrechnung"
              element={<AbrechnungRoute><GatedRoute><Abrechnung /></GatedRoute></AbrechnungRoute>} />
            <Route path="/favoriten" element={<FirmaFavoriten />} />
            <Route path="/team" element={<TeamRoute><TeamSeite /></TeamRoute>} />
            {/* ═══ Fahrer — Eigenes Geschäft ═══ */}
            <Route
              path="/eigene-auftraege"
              element={<KeinMitarbeiterRoute><FahrerEigeneAuftraege /></KeinMitarbeiterRoute>} />
            <Route path="/kunden" element={<KeinMitarbeiterRoute><FahrerKunden /></KeinMitarbeiterRoute>} />
            <Route
              path="/fahrtenbuch"
              element={<KeinMitarbeiterRoute><FahrerFahrtenbuch /></KeinMitarbeiterRoute>} />
            <Route path="/finanzen" element={<KeinMitarbeiterRoute><FahrerFinanzen /></KeinMitarbeiterRoute>} />
            <Route
              path="/rechnungen"
              element={<KeinMitarbeiterRoute><FahrerRechnungErstellen /></KeinMitarbeiterRoute>} />
            <Route path="/tourenplaner" element={<KeinMitarbeiterRoute><Tourenplaner /></KeinMitarbeiterRoute>} />
            {/* ═══ Marktplatz ═══ */}
            <Route
              path="/marktplatz"
              element={<GatedRoute><Marktplatz /></GatedRoute>} />
            <Route
              path="/meine-angebote"
              element={<GatedRoute><MeineAngebote /></GatedRoute>} />
            <Route
              path="/laufende-auftraege"
              element={<GatedRoute><FahrerAuftraege /></GatedRoute>} />
            <Route
              path="/zertifikate"
              element={<KeinMitarbeiterRoute><GatedRoute><FahrerZertifikate /></GatedRoute></KeinMitarbeiterRoute>} />
            <Route
              path="/auszahlung"
              element={<KeinMitarbeiterRoute><GatedRoute><Auszahlung /></GatedRoute></KeinMitarbeiterRoute>} />
            {/* ═══ Gemeinsam ═══ */}
            <Route path="/abo" element={<KeinMitarbeiterRoute><AboPage /></KeinMitarbeiterRoute>} />
            <Route path="/profil" element={<ProfilRoute />} />
            <Route path="/bewertungen" element={<BewertungenPage />} />
            <Route path="/rote-liste" element={<KeinMitarbeiterRoute><RoteListe /></KeinMitarbeiterRoute>} />
            <Route path="/ueber-uns" element={<UeberUns />} />
            {/* ═══ Admin (rollengeschützt) ═══ */}
            <Route
              path="/admin"
              element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route
              path="/admin/users"
              element={<AdminRoute><AdminUsers /></AdminRoute>} />
            {/* /admin/kyc entfiel am 16.08.2026 — die Verifizierungs-Dokumente
                sind jetzt ein Tab in der Nutzerkarte. Die Route leitet weiter,
                damit alte Lesezeichen und Links aus Mails nicht ins Leere
                laufen; der Filter zeigt direkt die Nutzer mit offener
                Prüfarbeit. */}
            <Route
              path="/admin/kyc"
              element={<Navigate to="/admin/users?filter=kyc-aufmerksam" replace />} />
            <Route
              path="/admin/auftraege"
              element={<AdminRoute><AdminAuftraege /></AdminRoute>} />
            <Route
              path="/admin/abos"
              element={<AdminRoute><AdminAbos /></AdminRoute>} />
            <Route
              path="/admin/reengagement"
              element={<AdminRoute><AdminReEngagement /></AdminRoute>} />
            <Route
              path="/admin/map"
              element={<AdminRoute><AdminMap /></AdminRoute>} />
            <Route
              path="/admin/monitoring"
              element={<AdminRoute><AdminMonitoring /></AdminRoute>} />
            <Route
              path="/admin/usage"
              element={<AdminRoute><AdminUsageAnalytics /></AdminRoute>} />
            <Route
              path="/admin/health"
              element={<AdminRoute><AdminHealthCheck /></AdminRoute>} />
            <Route
              path="/admin/funnel"
              element={<AdminRoute><AdminConversionFunnel /></AdminRoute>} />
            <Route
              path="/admin/blacklist"
              element={<AdminRoute><AdminBlacklist /></AdminRoute>} />
            <Route
              path="/admin/bewertungen"
              element={<AdminRoute><AdminBewertungen /></AdminRoute>} />
            <Route
              path="/admin/finanzen"
              element={<AdminRoute><AdminFinanzen /></AdminRoute>} />
            <Route
              path="/admin/settings"
              element={<AdminRoute><AdminSettings /></AdminRoute>} />
            <Route
              path="/admin/audit"
              element={<AdminRoute><AdminAuditLog /></AdminRoute>} />
            {/* ═══ Fallbacks ═══ */}
            {/* Rollenbasierte Landung (04.06.2026): Fahrer → Marktplatz,
                Firma → Dashboard, Admin → Adminbereich. Gleiche Logik wie
                der /login-Redirect in App.tsx (startPath). */}
            <Route
              index={true}
              element={<Navigate to={startPath(user?.rolle)} replace={true} />} />
            <Route
              path="*"
              element={<Navigate to={startPath(user?.rolle)} replace={true} />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
  // FahrerDataProvider nur für Fahrer mounten (lädt Kunden, Eigene Aufträge, etc.)
  if (isFahrer && user.id) {
    return <FahrerDataProvider userId={user.id}>{content}</FahrerDataProvider>;
  }
  return content;
}

export default ProtectedRoutes;
